<template>
    <div>
        <TitleBar>
            <template v-slot:title><translate>Cash Flow</translate></template>
            <template v-slot:buttons>
                <GekkoButton 
                    :text="$gettext('All reports')"
                    :to="{ name: 'Reports' }"
                    icon="fa fa-chart-column"/>
            </template>
        </TitleBar>

        <div v-if="!$store.getters['getIsPremium']" class="gk-tip gk-tip--warning gk-tip--caution my-4" :style="tipCssVars">
            <span v-translate>This report is based on fake data, please feel free to play with it and </span>
            <a href="/en/subscriptions/manage/">{{ $gettext("upgrade to premium.") }}</a>
        </div>

        <FilterBar
            cookieName="CashflowFilterOptionsVue"
            :mainFilters="['CashflowDateRangeSelector']"
            filterBarType="cash-flow"
            @change="filtersChanged">
            <template v-slot:right-bar>
                <BankSelector :bankAccountList="bankAccounts" @bankAccountSelected="onBankAccountSelected"/>
            </template>
        </FilterBar>

        <CashflowReportChart :chartData="chartData" :loading="loading"/>
        
        <CashFlowTable :labelColText="getLabelColText" :bankData="bankData" :loading="loading"/>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

import TitleBar from '@/components/TitleBar'
import FilterBar from '@/components/list_filters/FilterBar'
import CashFlowTable from '@/components/cash_flow/CashFlowTable'
import CashflowReportChart from '@/components/cash_flow/CashflowReportChart'
import GekkoButton from '@/components/GekkoButton'
import BankService from '@/store/services/bank.service.js'
import BankTransactionService from '@/store/services/bank.transaction.service'
import BankSelector from '@/components/cash_flow/BankSelector.vue'

export default {
    name: 'CashFlow',
    components: {TitleBar, FilterBar, GekkoButton, CashFlowTable, CashflowReportChart, BankSelector},
    data() {
        return {
            bankData: null,
            groupBy: 'day',
            selectedMonth: 0,
            selectedYear: (new Date()).getFullYear(),
            chartData: null,
            bankAccounts: [],
            bankAccount: null,
            loading: null
        }
    },
    computed: {
        ...mapGetters({ 
            getCompanyId: 'main/getCompanyId'
        }),
        getLabelColText() {
            if (this.groupBy === 'day')
                return this._getMonthLabel(this.selectedMonth)
            return this.selectedYear
        },
        tipCssVars() {
            return `--cautiontext: '${ this.$gettext('Gekko premium tip') }'`
        }
    },
    created() {
        this.getData()
    },
    methods: {
        endOf(date) {
            const year = date.getFullYear()
            const month = date.getMonth()
            return new Date(year, month + 1, 0)
        },
        diff(date1, date2) {
            return date1 - date2
        },
        getData() {
            if (this.$store.getters['getIsPremium']) this.getRealData()
            else this.getFakeData()
        },
        getFakeData() {
            this.bankData = [
                {label: this._getMonthLabel(0), cashflow: 1000, delta: 200},
                {label: this._getMonthLabel(1), cashflow: 1200, delta: 200},
                {label: this._getMonthLabel(2), cashflow: 1400, delta: 200},
                {label: this._getMonthLabel(3), cashflow: 1200, delta: -200},
                {label: this._getMonthLabel(4), cashflow: 1100, delta: -100},
                {label: this._getMonthLabel(5), cashflow: 1300, delta: 200},
                {label: this._getMonthLabel(6), cashflow: 1500, delta: 200},
                {label: this._getMonthLabel(7), cashflow: 1600, delta: 100},
                {label: this._getMonthLabel(8), cashflow: 1300, delta: -300},
                {label: this._getMonthLabel(9), cashflow: 1500, delta: 200},
                {label: this._getMonthLabel(10), cashflow: 1700, delta: 200},
                {label: this._getMonthLabel(11), cashflow: 1800, delta: 100}
            ]
            this._convertToChartData()
        },
        getRealData() {
            this.chartData = null
            this.loading = true

            BankService.list({ company_profile: this.getCompanyId }).then((response) => {
                if(response.data.length) {
                    
                    this.bankAccounts = response.data
                    if(!this.bankAccount) {
                        this.bankAccount = this.bankAccounts[0]
                    }
                    
                    this.balance = this._parseBalance(this.bankAccount)
                    this.reportTimeFrame = this._getReportTimeFrame(this.groupBy)
                    this.emptyTS = this._getChartEmptyTimeSeries(this.reportTimeFrame, this.groupBy)

                    let startBalancePromise = this._getStartBalance(this.balance, this.reportTimeFrame)
                    let totalsPromise = this._getCashFlowTotals(this.reportTimeFrame, this.groupBy)

                    let accountActiveTimeFramePromise = BankTransactionService.activeTimeRange(this.bankAccount.id)

                    Promise.all([startBalancePromise, totalsPromise, accountActiveTimeFramePromise]).then((res) => {
                        let startBalance = res[0]
                        let totals = res[1]

                        let accountActiveTimeFrame = {
                            start: Date.parse(res[2].data[0]),
                            end: Date.parse(res[2].data[1])
                        }

                        this.operativeTimeFrame = this._getOperativeTimeFrame(accountActiveTimeFrame, this.reportTimeFrame)
                        this.activeYears = this._getActiveYears(accountActiveTimeFrame)

                        let cashflowTS = []
                        if (startBalance !== 'unavailable') {
                            cashflowTS = this._buildIntervals(startBalance, totals, this.groupBy)

                            this.bankData = this.emptyTS.map((entry, i) => {
                                let loadedEntry = {}

                                loadedEntry.label = this.groupBy === 'day' ? Date.parse(entry.date).getDate() : cashflowTS[i].label
                                
                                if (Object.prototype.hasOwnProperty.call(cashflowTS[i], 'cashflow')) loadedEntry.cashflow = cashflowTS[i].cashflow
                                if (Object.prototype.hasOwnProperty.call(cashflowTS[i], 'delta')) loadedEntry.delta = cashflowTS[i].delta
                                
                                return loadedEntry
                            })
                            
                            this._convertToChartData()

                            this.loading = false
                        } else {
                            this.bankData = null
                            this.loading = false
                        }
                    })
                } else {
                    this.bankData = null
                    this.loading = false
                }
            })
        },
        _convertToChartData() {
            this.chartData = {
                labels: this.bankData.map((x) => x.label),
                
                datasets: [{
                    type: 'line',
                    label: this.$gettext('Balance at the end of the period:'),
                    data: this.bankData.map((x) => parseInt(x.cashflow)),
                    borderColor: '#37A0CD',
                },{
                    type: 'bar',
                    label: this.$gettext('Cashflow:'),
                    data: this.bankData.map((x) => parseInt(x.delta)),
                    backgroundColor: '#ac8255',
                }]
            }
        },
        _getReportTimeFrame(periodType) {
            let timeFrame = {}

            if (periodType === 'month') {
                timeFrame.start = Date.parse(`${this.selectedYear}-01-01`)
                timeFrame.end =  Date.parse(`${this.selectedYear}-12-31`)
            }

            if (periodType === 'day') {
                let monthStartDate = new Date();
                monthStartDate.setFullYear(this.selectedYear, this.selectedMonth, 1);
                timeFrame.start = new Date(monthStartDate.getFullYear(), monthStartDate.getMonth(), monthStartDate.getDate());
                timeFrame.end = this.endOf(monthStartDate)
            }

            return timeFrame
        },
        _buildIntervals(startBalance, totals, periodType) {
            let intervals = []
            this.currentBalance = startBalance

            if (periodType === 'month') {
                intervals = totals.reduce((intervals, total, i) => {
                    let label = this._getMonthLabel(i)

                    let nextCashFlowEntry = {
                        label: label
                    }

                    let firstDayOfMonth = Date.parse([this.selectedYear, i+1, 1].join('-'))
                    let monthPeriod = {
                        start: firstDayOfMonth,
                        end: (new Date(firstDayOfMonth)).setMonth(firstDayOfMonth.getMonth() + 1)
                    }

                    this.currentBalance += total
                    nextCashFlowEntry.cashflow = this.currentBalance
                    nextCashFlowEntry.delta = total

                    intervals.push(nextCashFlowEntry)
                    return intervals
                }, [])
            }

            if (periodType === 'day') {
                intervals = totals.reduce((intervals, total, i) => {
                    let nextEntry = JSON.parse(JSON.stringify(total))

                    this.currentBalance += total.amount
                    nextEntry.amount = this.currentBalance
                    nextEntry.delta = total.amount
                    intervals.push(nextEntry)
                    return intervals
                }, []).map((entry) => {
                    let interval = {
                        label: Date.parse(entry.date).getDate()
                    }
                    let dayPeriod = {
                        start: Date.parse(entry.date),
                        end: Date.parse(entry.date)
                    }
                    
                    interval.cashflow = entry.amount
                    interval.delta = entry.delta

                    return interval
                })
            }

            delete this.currentBalance

            return intervals
        },
        _getOperativeTimeFrame(dataTimeFrame, reportTimeFrame) {
            let start = (this.diff(reportTimeFrame.start, dataTimeFrame.end) >= 0)
                        && (this.diff(reportTimeFrame.start, dataTimeFrame.start) <= 0) ?
                        reportTimeFrame.start : dataTimeFrame.start
            
            let end = (this.diff(reportTimeFrame.end, dataTimeFrame.start) <= 0)
                        && (this.diff(reportTimeFrame.end, dataTimeFrame.end) > 0) ?
                        reportTimeFrame.end : dataTimeFrame.end

            return {
                start: start,
                end: end
            }
        },
        _getCashFlowTotals(timeFrame, groupType) {
            let timeFrameCopy = {start: new Date(timeFrame.start), end: new Date(timeFrame.end)}

            timeFrameCopy.start.setDate(timeFrame.start.getDate())
            timeFrameCopy.end.setDate(timeFrame.end.getDate()+1)
            let reqFilters = {
                account: this.bankAccount.id,
                start_date: timeFrameCopy.start.getFullYear() + "-" + (timeFrameCopy.start.getMonth()+1)+"-"+timeFrameCopy.start.getDate(),
                end_date: timeFrameCopy.end.getFullYear() + "-" + (timeFrameCopy.end.getMonth()+1)+"-"+timeFrameCopy.end.getDate(),
            }
            if (groupType) {
                reqFilters.group_by = groupType
            }
            return BankTransactionService.total(reqFilters).then((res) => {
                let data = res.data

                if (groupType === 'day') {
                    let totals = []
                    let currentDate = null
                    let i = 0

                    for (currentDate = new Date(timeFrameCopy.start.valueOf()); this.diff(timeFrameCopy.end, currentDate) > 0; currentDate.setDate(currentDate.getDate() + 1)) {
                        let currentDateCopy = new Date(currentDate);
                        const offset = currentDate.getTimezoneOffset();
                        currentDateCopy.setTime(currentDateCopy.getTime() - offset * 60 * 1000);
                        let parsedCurrentDate = currentDateCopy.toISOString().split('T')[0];
                        let nextEntry = {
                            date: currentDate
                        }

                        if (i < data.length && (data[i].date === parsedCurrentDate)) {
                            nextEntry.amount = data[i].total
                            i++
                        } else {
                            nextEntry.amount = 0
                        }
                        totals.push(nextEntry)
                    }

                    return totals
                } else {
                    return data
                }
            })
        },
        _getStartBalance(balance, timeFrame) {
            const millisecondsDiff = balance.date - timeFrame.start

            if (millisecondsDiff < 0) {
                return 'unavailable'
            }
            
            let gapTimeFrame = {
                start: timeFrame.start,
                end: balance.date
            }

            return this._getCashFlowTotals(gapTimeFrame).then((gapTotal) => {
                return (this.balance.amount - gapTotal)
            })
        },
        _parseBalance(bankAccount) {
            return {
                amount: parseFloat(bankAccount.balance),
                date: Date.parse(bankAccount.balance_date)
            }
        },
        _getActiveYears(operativeTimeFrame) {
            let years = []
            let startYear
            let endYear
            if (operativeTimeFrame.start || operativeTimeFrame.end) {
                startYear = operativeTimeFrame.start.getFullYear()
                endYear = operativeTimeFrame.end.getFullYear()
            } else {
                startYear = (new Date()).getFullYear()
                endYear = (new Date()).getFullYear()
            }
            let year = null
            for (year = startYear; year <= endYear; year++) {
                years.push(year)
            }
            return years.reverse()
        },
        _getChartEmptyTimeSeries(timeFrame, groupType) {
            let timeSeries = []
            let currentDate = null

            if (groupType === 'day') {
                for (currentDate = new Date(timeFrame.start.valueOf()); currentDate <= timeFrame.end; currentDate.setDate(currentDate.getDate() + 1)) {
                    timeSeries.push({ date: new Date(currentDate.valueOf()) })
                }
            }

            if (groupType === 'month') {
                for (currentDate = new Date(timeFrame.start.valueOf()); this.diff(currentDate, timeFrame.end) <= 0 ; currentDate.setMonth(currentDate.getMonth() + 1)) {
                    timeSeries.push({ date: new Date(currentDate.valueOf()) })
                }
            }

            return timeSeries
        },
        filtersChanged(filters) {
            if (filters.cashflow_options) {
                this.groupBy = filters.cashflow_options.groupBy
                this.selectedMonth = filters.cashflow_options.month
                this.selectedYear = filters.cashflow_options.year
                this.getData()
            }
        },
        _getMonthLabel(number) {
            switch (number) {
                case 0:
                    return this.$gettext('January')
                case 1:
                    return this.$gettext('February')
                case 2:
                    return this.$gettext('March')
                case 3:
                    return this.$gettext('April')
                case 4:
                    return this.$gettext('May')
                case 5:
                    return this.$gettext('June')
                case 6:
                    return this.$gettext('July')
                case 7:
                    return this.$gettext('August')
                case 8:
                    return this.$gettext('September')
                case 9:
                    return this.$gettext('October')
                case 10:
                    return this.$gettext('November')
                case 11:
                    return this.$gettext('December')
            }
        },
        onBankAccountSelected(selectedBankAccount) {
            this.bankAccount = selectedBankAccount
            this.getData()
        }
    }
}
</script>

<style scoped>
</style>